/**
 * Les deux collections (GDD §10). Module pur.
 *
 * Elles ne se recouvrent pas, et c'est tout l'intérêt :
 *
 * | | Archimonstres | Avis de recherche |
 * |---|---|---|
 * | rareté | fréquent | 0,05 % par kill |
 * | présence requise | **oui** | non |
 * | ce que ça mesure | le volume | la **complétion** |
 * | comportement payé | être au clavier | laisser tourner des mois |
 */
import type { Contenu } from "./contenu.ts"
import { monstre } from "./contenu.ts"

/** Chance qu'un archimonstre surgisse, par kill. */
export const TAUX_ARCHIMONSTRE = 0.005
/** Chance qu'un avis de recherche surgisse, par kill (GDD §10). */
export const TAUX_RECHERCHE = 0.0005
/** Durée pendant laquelle un archimonstre reste capturable, en ms. */
export const DUREE_ARCHIMONSTRE_MS = 3 * 60_000
/** Écart de niveau toléré pour qu'une apparition soit « du coin ». */
const ECART_NIVEAU = 15

export interface Apparition {
  monstre: number
  expire: number
}

/**
 * Tire un candidat de niveau comparable, en privilégiant ceux qui manquent
 * encore à la collection.
 *
 * Sans cette pondération, la fin de collection se traîne sur des doublons : le
 * dernier d'une tranche coûterait autant que les huit premiers réunis.
 */
/**
 * Part de chance laissée à une cible hors de la fenêtre de niveau.
 *
 * Un vingtième, pas un septième : les cibles lointaines sont nombreuses, et à
 * un septième chacune elles pesaient collectivement autant que la seule cible du
 * bon niveau. Le voisinage doit rester la règle, le lointain l'exception.
 */
const LOIN = 1 / 20

export function tirer(
  table: Record<string, { id: number; niveau: number; zone?: number | null }>,
  captures: number[], niveau: number, rng: () => number, zone?: number | null,
): number | null {
  const tous = Object.values(table)
  // La **zone** d'abord : on ne croise pas un archimonstre de Cania en farmant
  // Incarnam. Sans ce filtre, la collection entière se remplissait n'importe où
  // et perdait tout lien avec le monde.
  const ici = zone != null ? tous.filter((x) => x.zone === zone) : []
  const pool = ici.length > 0 ? ici : tous
  if (pool.length === 0) return null

  // **L'écart de niveau défavorise, il n'exclut plus.**
  //
  // Le filtre était sec : hors de la fenêtre, aucune chance. Or une zone n'a pas
  // forcément une famille farmable à tous les niveaux — Enutrosor n'en a qu'à 92
  // et 200, Xélorium à 112 et 200 — si bien que l'Aigripoil (150) et le Hin (170)
  // ne pouvaient **jamais** être tirés, quoi qu'on y farme. Balbouz l'a repéré le
  // 31/08/2026 : « pas sûr qu'on puisse avoir tous les avis de recherche ».
  //
  // Le voisinage garde donc tout son poids — on croise surtout ce qui rôde à
  // notre niveau — mais le lointain garde une petite chance plutôt que zéro.
  const pris = new Set(captures)
  const poids = pool.map((x) => {
    const proche = Math.abs(x.niveau - niveau) <= ECART_NIVEAU
    return (pris.has(x.id) ? 1 : 6) * (proche ? 1 : LOIN)
  })
  const total = poids.reduce((a, b) => a + b, 0)
  let tirage = rng() * total
  for (let i = 0; i < pool.length; i++) {
    tirage -= poids[i]!
    if (tirage <= 0) return pool[i]!.id
  }
  return pool[pool.length - 1]!.id
}

/** Un archimonstre expiré n'est plus capturable : c'est le prix de l'absence. */
export const expiree = (a: Apparition | null | undefined, maintenant: number) =>
  Boolean(a) && maintenant >= a!.expire

export function nomApparition(c: Contenu, a: Apparition | null): string {
  return a ? monstre(c, a.monstre)?.nom ?? "?" : ""
}

/** Progression d'une collection, pour l'affichage. */
export const avancement = (table: Record<string, unknown>, captures: number[]) => ({
  pris: captures.length,
  total: Object.keys(table).length,
  pct: Object.keys(table).length ? Math.round((captures.length / Object.keys(table).length) * 100) : 0,
})
